import React, { useState } from 'react';
import { useApiClient } from '@marageti/z4-lib';
import { formatMaskedPhone, MAX_OTP_FAILED_ATTEMPTS } from '../../helpers';
import useLoginAuth from '../../use-login-auth';
import SmsVerificationView from './sms-verification-view';

export interface SmsVerificationProps {
  email: string;
  maskedPhoneLast4: string;
  cooldownSeconds: number;
  startCooldown: () => void;
  onChangeEmail: () => void;
  onUsePasswordVerification: () => void;
  onUseEmailVerification: () => void | Promise<void>;
}

const SmsVerification = ({
  email,
  maskedPhoneLast4,
  cooldownSeconds,
  startCooldown,
  onChangeEmail,
  onUsePasswordVerification,
  onUseEmailVerification,
}: SmsVerificationProps): React.ReactElement => {
  const apiClient = useApiClient();
  const { login } = useLoginAuth();
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const showTooManyAttempts = failedAttempts >= MAX_OTP_FAILED_ATTEMPTS;

  const handleResendCode = async () => {
    if (cooldownSeconds > 0) {
      return;
    }
    setVerificationError(false);
    setFailedAttempts(0);
    startCooldown();
    await apiClient.authClient
      .postVerification({ channel: 'sms', email })
      .catch(console.warn);
  };

  const handleVerify = async (code: string) => {
    if (showTooManyAttempts) {
      return;
    }
    setIsVerifying(true);
    setVerificationError(false);
    const minCheckDelay = new Promise<void>((resolve) => {
      setTimeout(resolve, 1500);
    });

    let failed = false;
    try {
      await login({ channel: 'sms', email, code });
    } catch {
      failed = true;
    }

    await minCheckDelay;
    if (failed) {
      setVerificationError(true);
      setFailedAttempts((prev) => prev + 1);
    }
    setIsVerifying(false);
  };

  const handleChangeEmail = () => {
    setFailedAttempts(0);
    onChangeEmail();
  };

  return (
    <SmsVerificationView
      maskedPhone={formatMaskedPhone(maskedPhoneLast4)}
      isVerifying={isVerifying}
      verificationError={verificationError}
      cooldownSeconds={cooldownSeconds}
      showTooManyAttempts={showTooManyAttempts}
      onVerify={handleVerify}
      onClearError={() => setVerificationError(false)}
      onResendCode={handleResendCode}
      onUseEmailVerification={onUseEmailVerification}
      onChangeEmail={handleChangeEmail}
      onUsePasswordVerification={onUsePasswordVerification}
    />
  );
};

export default SmsVerification;
